import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { projects, projectCategories, cities } from '@/mocks/projects';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageSEO from '@/components/base/PageSEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [city, setCity] = useState('all');
  const { ref, isVisible } = useScrollReveal();

  const filtered = projects.filter((p) => {
    const matchesSearch =
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.location.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      category === 'all'
        ? true
        : category === 'featured'
        ? p.featured
        : p.category.toLowerCase() === category || p.subcategory === category;
    const matchesCity = city === 'all' ? true : p.city === city;
    return matchesSearch && matchesCategory && matchesCity;
  });

  return (
    <div className="relative min-h-screen bg-ivory-50">
      <PageSEO
        title="Our Projects — Verified Property Investments | Sunshine Real Estate Pakistan"
        description="Explore verified residential and commercial real estate projects across Islamabad, Karachi, and Lahore. Premium plots, apartments, and investment opportunities with Sunshine Real Estate Marketing."
        keywords="real estate projects Pakistan, Islamabad property projects, residential plots, commercial property, investment opportunities Pakistan, Margala Garden, Blue World City"
        canonicalPath="/projects"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Our Projects — Verified Property Investments | Sunshine Real Estate',
          description: 'Explore verified residential and commercial real estate projects across Pakistan. Premium plots, apartments, and investment opportunities.',
          url: `${SITE_URL}/projects`,
          isPartOf: {
            '@type': 'WebSite',
            name: 'Sunshine Real Estate Marketing SMC (Pvt.) Ltd.',
            url: SITE_URL,
          },
        }}
      />
      <Navbar />

      {/* Hero Banner */}
      <div className="relative h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden pt-16 md:pt-20">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20luxury%20residential%20skyline%20with%20premium%20apartments%20and%20green%20landscaping%20at%20dusk%20golden%20hour%20Islamabad%20Pakistan%20cinematic%20wide%20shot%20real%20estate&width=1920&height=500&seq=701&orientation=landscape"
          alt="Our Projects — Sunshine Real Estate Pakistan"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-charcoal-400/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-3">
            Our Projects
          </h1>
          <p className="text-sm md:text-base text-white/80 max-w-lg mx-auto">
            Explore verified residential and commercial investment opportunities across Pakistan.
          </p>
        </div>
      </div>

      <main className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        {/* Filters */}
        <div
          ref={ref}
          className={`flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-8 md:mb-10 ${
            isVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-50" />
            <input
              type="text"
              placeholder="Search projects or locations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-ivory-300 rounded-xl text-sm text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange transition-all"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-3 bg-white border border-ivory-300 rounded-xl text-sm text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 cursor-pointer"
            >
              {projectCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="px-4 py-3 bg-white border border-ivory-300 rounded-xl text-sm text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 cursor-pointer"
            >
              {cities.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-5 text-sm text-charcoal-50">
          Showing <span className="font-semibold text-charcoal-300">{filtered.length}</span> project{filtered.length !== 1 ? 's' : ''}
        </div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((project) => (
              <div
                key={project.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={() => { navigate(`/projects/${project.id}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >
                <div className="relative h-52 md:h-56 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal-400/60 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="px-3 py-1 bg-brand-orange text-white text-xs font-semibold rounded-full">
                      {project.category}
                    </span>
                    {project.featured && (
                      <span className="px-3 py-1 bg-brand-green text-white text-xs font-semibold rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 flex items-center gap-1.5 text-white text-sm">
                    <i className="ri-map-pin-line" />
                    {project.location}
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-base md:text-lg font-bold text-charcoal-300 leading-snug mb-2 group-hover:text-brand-orange transition-colors line-clamp-2">
                    {project.title}
                  </h3>
                  <p className="text-sm text-charcoal-50 leading-relaxed mb-4 line-clamp-2">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.plotSizes.slice(0, 3).map((size) => (
                      <span key={size} className="px-2.5 py-1 bg-ivory-100 text-charcoal-100 text-xs font-medium rounded-md">
                        {size}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-ivory-200">
                    <span className="text-sm font-semibold text-brand-orange">From {project.priceFrom}</span>
                    <span className="flex items-center gap-1 text-sm font-medium text-charcoal-100 group-hover:text-brand-orange transition-colors">
                      View Details <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-ivory-200 mx-auto mb-4">
              <i className="ri-search-line text-2xl text-charcoal-50" />
            </div>
            <h3 className="text-lg font-bold text-charcoal-300 mb-2">No projects found</h3>
            <p className="text-sm text-charcoal-50">Try adjusting your search or filters.</p>
          </div>
        )}
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}